import React, { useState, useEffect } from 'react';
import {
  UzairueContact,
  INITIAL_UZAIRUE_VILLAGES,
  INITIAL_DIASPORA_CHAPTERS,
} from '../../data/initialContactsData';
import {
  getContacts,
  addContact,
  deleteContact,
  updateContact,
  generateCsvTemplate,
} from '../../utils/portalStorage';
import ContactUploadModal from './ContactUploadModal';

export const ContactsDirectory: React.FC = () => {
  const [contacts, setContacts] = useState<UzairueContact[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVillage, setSelectedVillage] = useState<string>('all');
  const [selectedChapter, setSelectedChapter] = useState<string>('all');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('table');

  // Modals state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // New Contact Form State
  const [newContact, setNewContact] = useState<Omit<UzairueContact, 'id' | 'createdAt'>>({
    fullName: '',
    title: '',
    village: 'Jattu-Uzairue',
    diasporaChapter: 'Uzairue Local Govt / Edo State',
    email: '',
    phone: '',
    whatsappEnabled: true,
    role: 'General Diaspora Member',
    organization: '',
    verified: true,
    notes: '',
  });

  const loadData = () => {
    setContacts(getContacts());
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newContact.fullName || !newContact.email) {
      alert('Full Name and Email Address are required.');
      return;
    }
    addContact(newContact);
    setIsAddModalOpen(false);
    loadData();
    // Reset form
    setNewContact({
      fullName: '',
      title: '',
      village: 'Jattu-Uzairue',
      diasporaChapter: 'Uzairue Local Govt / Edo State',
      email: '',
      phone: '',
      whatsappEnabled: true,
      role: 'General Diaspora Member',
      organization: '',
      verified: true,
      notes: '',
    });
  };

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to remove ${name} from the directory?`)) {
      deleteContact(id);
      loadData();
    }
  };

  const toggleVerify = (contact: UzairueContact) => {
    updateContact(contact.id, { verified: !contact.verified });
    loadData();
  };

  // Filtered Contacts
  const filteredContacts = contacts.filter((c) => {
    const matchesSearch =
      c.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.phone.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.organization && c.organization.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (c.notes && c.notes.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesVillage = selectedVillage === 'all' || c.village === selectedVillage;
    const matchesChapter = selectedChapter === 'all' || c.diasporaChapter === selectedChapter;
    const matchesRole = selectedRole === 'all' || c.role === selectedRole;

    return matchesSearch && matchesVillage && matchesChapter && matchesRole;
  });

  // Export Filtered Contacts to CSV
  const exportToCsv = () => {
    const header = 'Full Name,Title,Village,Diaspora Chapter,Email,Phone,WhatsApp,Role,Organization,Verified\n';
    const rows = filteredContacts.map(
      (c) =>
        `"${c.fullName}","${c.title || ''}","${c.village}","${c.diasporaChapter}","${c.email}","${c.phone}","${
          c.whatsappEnabled ? 'Yes' : 'No'
        }","${c.role}","${c.organization || ''}","${c.verified ? 'Yes' : 'No'}"`
    );
    const blob = new Blob([header + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `uzairue_diaspora_directory_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export Filtered Contacts to VCF (vCard)
  const exportToVcf = () => {
    const vcfBlocks = filteredContacts.map((c) => {
      const formattedPhone = c.phone.replace(/[^0-9+]/g, '');
      return [
        'BEGIN:VCARD',
        'VERSION:3.0',
        `FN:${c.title ? c.title + ' ' : ''}${c.fullName}`,
        `N:${c.fullName};;;;`,
        `EMAIL:${c.email}`,
        `TEL;TYPE=CELL:${formattedPhone}`,
        `ORG:Uzairue Kingdom;${c.organization || c.diasporaChapter}`,
        `TITLE:${c.role}`,
        `NOTE:Uzairue Village: ${c.village} | Chapter: ${c.diasporaChapter}`,
        'END:VCARD',
      ].join('\r\n');
    });

    const blob = new Blob([vcfBlocks.join('\r\n\r\n')], { type: 'text/vcard;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `uzairue_diaspora_contacts_${new Date().toISOString().slice(0, 10)}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Directory Action Header */}
      <div className="bg-slate-900 border border-slate-800 p-5 rounded-2xl shadow-xl flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 text-white">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-black font-serif text-amber-300 uppercase tracking-wide">
              Global Uzairue Diaspora Contacts Directory
            </h2>
            <span className="bg-emerald-900 text-emerald-300 text-[10px] px-2 py-0.5 rounded-full font-mono font-bold">
              {contacts.length} Registered Members
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Global register of Uzairue sons, daughters, royal elders, and festival delegates worldwide
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={generateCsvTemplate}
            className="bg-slate-800 hover:bg-slate-700 text-amber-300 border border-amber-500/30 text-xs font-bold px-3 py-2 rounded-xl transition-all cursor-pointer flex items-center gap-1.5"
            title="Download blank CSV template for uploading contacts"
          >
            <span>📥</span> CSV Template
          </button>

          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="bg-emerald-800 hover:bg-emerald-700 text-emerald-100 border border-emerald-500/40 text-xs font-bold px-3 py-2 rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shadow"
          >
            <span>📂</span> Bulk Upload CSV
          </button>

          <button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs px-4 py-2 rounded-xl shadow-md transition-all cursor-pointer flex items-center gap-1.5"
          >
            <span>➕</span> Add Contact
          </button>

          <div className="flex items-center bg-slate-800 p-1 rounded-xl border border-slate-700">
            <button
              onClick={exportToCsv}
              className="text-[11px] font-bold text-slate-200 hover:text-white px-2.5 py-1 rounded hover:bg-slate-700 cursor-pointer"
              title="Export filtered directory to CSV file"
            >
              CSV
            </button>
            <span className="text-slate-600">|</span>
            <button
              onClick={exportToVcf}
              className="text-[11px] font-bold text-emerald-300 hover:text-emerald-200 px-2.5 py-1 rounded hover:bg-slate-700 cursor-pointer"
              title="Export filtered directory to VCF (vCard) for mobile contact sync"
            >
              vCard
            </button>
          </div>
        </div>
      </div>

      {/* Search & Multi-Level Filters Bar */}
      <div className="bg-slate-950 border border-slate-800 p-4 rounded-xl grid grid-cols-1 md:grid-cols-4 gap-3 text-white">
        {/* Search */}
        <div className="md:col-span-1 relative">
          <input
            type="text"
            placeholder="Search name, email, phone, notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400"
          />
        </div>

        {/* Village Filter */}
        <div>
          <select
            value={selectedVillage}
            onChange={(e) => setSelectedVillage(e.target.value)}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs text-amber-300 focus:outline-none focus:border-amber-400 cursor-pointer"
          >
            <option value="all">All Villages / Sub-Clans ({INITIAL_UZAIRUE_VILLAGES.length})</option>
            {INITIAL_UZAIRUE_VILLAGES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </div>

        {/* Diaspora Chapter Filter */}
        <div>
          <select
            value={selectedChapter}
            onChange={(e) => setSelectedChapter(e.target.value)}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs text-amber-300 focus:outline-none focus:border-amber-400 cursor-pointer"
          >
            <option value="all">All Diaspora Chapters ({INITIAL_DIASPORA_CHAPTERS.length})</option>
            {INITIAL_DIASPORA_CHAPTERS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* View Switcher & Role Filter */}
        <div className="flex items-center gap-2">
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs text-amber-300 focus:outline-none focus:border-amber-400 cursor-pointer"
          >
            <option value="all">All Roles</option>
            <option value="Royal Elder">Royal Elder</option>
            <option value="Diaspora Delegate">Diaspora Delegate</option>
            <option value="Festival Coordinator">Festival Coordinator</option>
            <option value="Youth Leader">Youth Leader</option>
            <option value="General Diaspora Member">General Diaspora Member</option>
          </select>

          <div className="flex bg-slate-900 p-1 rounded-lg border border-slate-800">
            <button
              onClick={() => setViewMode('table')}
              className={`p-1.5 rounded text-xs font-bold cursor-pointer ${
                viewMode === 'table' ? 'bg-amber-500 text-slate-950' : 'text-slate-400'
              }`}
              title="Table View"
            >
              📋
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`p-1.5 rounded text-xs font-bold cursor-pointer ${
                viewMode === 'cards' ? 'bg-amber-500 text-slate-950' : 'text-slate-400'
              }`}
              title="Card View"
            >
              📇
            </button>
          </div>
        </div>
      </div>

      {/* Directory Table View */}
      {viewMode === 'table' ? (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-200">
              <thead className="bg-slate-950 text-slate-400 uppercase font-mono text-[10px] tracking-wider border-b border-slate-800">
                <tr>
                  <th className="p-3.5">Name & Title</th>
                  <th className="p-3.5">Village</th>
                  <th className="p-3.5">Diaspora Chapter</th>
                  <th className="p-3.5">Role</th>
                  <th className="p-3.5">Contact Details</th>
                  <th className="p-3.5 text-center">Status</th>
                  <th className="p-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filteredContacts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-slate-500 italic">
                      No matching Uzairue contacts found. Try adjusting your search query or filters.
                    </td>
                  </tr>
                ) : (
                  filteredContacts.map((contact) => (
                    <tr key={contact.id} className="hover:bg-slate-800/60 transition-colors">
                      <td className="p-3.5">
                        <div className="font-extrabold text-white text-sm">
                          {contact.title ? `${contact.title} ` : ''}
                          {contact.fullName}
                        </div>
                        {contact.organization && (
                          <div className="text-[11px] text-amber-300/90 font-medium">{contact.organization}</div>
                        )}
                      </td>
                      <td className="p-3.5 whitespace-nowrap">
                        <span className="bg-slate-950 text-emerald-300 px-2 py-0.5 rounded border border-emerald-900 text-[11px] font-bold">
                          {contact.village}
                        </span>
                      </td>
                      <td className="p-3.5 whitespace-nowrap text-slate-300">{contact.diasporaChapter}</td>
                      <td className="p-3.5 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wider ${
                            contact.role === 'Royal Elder'
                              ? 'bg-amber-900/80 text-amber-300 border border-amber-500/40'
                              : contact.role === 'Festival Coordinator'
                              ? 'bg-emerald-900/80 text-emerald-200 border border-emerald-600/40'
                              : 'bg-slate-800 text-slate-300'
                          }`}
                        >
                          {contact.role}
                        </span>
                      </td>
                      <td className="p-3.5">
                        <div className="font-mono text-xs text-slate-300">{contact.email}</div>
                        <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                          <span>{contact.phone || 'No phone'}</span>
                          {contact.whatsappEnabled && contact.phone && (
                            <a
                              href={`https://wa.me/${contact.phone.replace(/[^0-9]/g, '')}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-emerald-400 hover:text-emerald-300 font-bold ml-1 text-[10px] bg-emerald-950 px-1 rounded"
                            >
                              💬 WhatsApp
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="p-3.5 text-center">
                        <button
                          onClick={() => toggleVerify(contact)}
                          className={`px-2 py-0.5 rounded text-[10px] font-bold cursor-pointer ${
                            contact.verified
                              ? 'bg-emerald-900/90 text-emerald-300 border border-emerald-500/50'
                              : 'bg-slate-800 text-slate-500'
                          }`}
                        >
                          {contact.verified ? '✓ Verified' : 'Unverified'}
                        </button>
                      </td>
                      <td className="p-3.5 text-right whitespace-nowrap">
                        <button
                          onClick={() => handleDelete(contact.id, contact.fullName)}
                          className="text-slate-500 hover:text-red-400 p-1.5 transition-colors cursor-pointer"
                          title="Delete contact"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Directory Grid Cards View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredContacts.map((contact) => (
            <div
              key={contact.id}
              className="bg-slate-900 border border-slate-800 hover:border-amber-500/40 p-5 rounded-2xl shadow-xl space-y-3 transition-all relative"
            >
              <div className="flex justify-between items-start gap-2">
                <div>
                  <h3 className="font-extrabold text-white text-base font-serif">
                    {contact.title ? `${contact.title} ` : ''}
                    {contact.fullName}
                  </h3>
                  <span className="bg-emerald-950 text-emerald-300 text-[10px] px-2 py-0.5 rounded font-mono font-bold">
                    {contact.village}
                  </span>
                </div>
                <button
                  onClick={() => toggleVerify(contact)}
                  className={`px-2 py-0.5 rounded text-[10px] font-bold cursor-pointer ${
                    contact.verified
                      ? 'bg-emerald-900 text-emerald-300 border border-emerald-500/50'
                      : 'bg-slate-800 text-slate-500'
                  }`}
                >
                  {contact.verified ? '✓ Verified' : 'Unverified'}
                </button>
              </div>

              <div className="text-xs space-y-1 text-slate-300">
                <p>
                  <strong className="text-amber-300/80">Chapter:</strong> {contact.diasporaChapter}
                </p>
                <p>
                  <strong className="text-amber-300/80">Role:</strong> {contact.role}
                </p>
                {contact.organization && (
                  <p>
                    <strong className="text-amber-300/80">Org:</strong> {contact.organization}
                  </p>
                )}
                <p className="font-mono text-slate-400 truncate">{contact.email}</p>
                <p className="font-mono text-slate-400">{contact.phone || 'N/A'}</p>
              </div>

              {contact.notes && (
                <p className="text-[11px] bg-slate-950 p-2 rounded text-slate-400 italic">{contact.notes}</p>
              )}

              <div className="pt-2 border-t border-slate-800 flex justify-between items-center text-xs">
                {contact.whatsappEnabled && contact.phone ? (
                  <a
                    href={`https://wa.me/${contact.phone.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noreferrer"
                    className="bg-emerald-700 hover:bg-emerald-600 text-white font-bold px-3 py-1.5 rounded-lg text-xs transition-all flex items-center gap-1"
                  >
                    💬 WhatsApp Chat
                  </a>
                ) : (
                  <a
                    href={`mailto:${contact.email}`}
                    className="bg-slate-800 hover:bg-slate-700 text-amber-300 font-bold px-3 py-1.5 rounded-lg text-xs"
                  >
                    ✉️ Email
                  </a>
                )}

                <button
                  onClick={() => handleDelete(contact.id, contact.fullName)}
                  className="text-slate-500 hover:text-red-400 p-1 cursor-pointer"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload CSV Modal */}
      <ContactUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onImportSuccess={() => {
          setIsUploadModalOpen(false);
          loadData();
        }}
      />

      {/* Manual Add Contact Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-amber-500/40 w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden text-white">
            <div className="bg-slate-950 border-b border-slate-800 p-4 flex justify-between items-center">
              <h3 className="font-serif text-amber-300 font-bold text-sm uppercase">Add New Uzairue Contact</h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-slate-400 hover:text-white">
                ✕
              </button>
            </div>

            <form onSubmit={handleAddSubmit} className="p-5 space-y-3 max-h-[80vh] overflow-y-auto text-xs">
              <div>
                <label className="block text-slate-300 font-bold mb-1">Full Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g., Chief Braimah Momoh"
                  value={newContact.fullName}
                  onChange={(e) => setNewContact({ ...newContact, fullName: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Traditional Title</label>
                  <input
                    type="text"
                    placeholder="e.g., Daudu / High Chief"
                    value={newContact.title}
                    onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Role / Position</label>
                  <select
                    value={newContact.role}
                    onChange={(e) => setNewContact({ ...newContact, role: e.target.value as any })}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-amber-300"
                  >
                    <option value="Royal Elder">Royal Elder</option>
                    <option value="Diaspora Delegate">Diaspora Delegate</option>
                    <option value="Festival Coordinator">Festival Coordinator</option>
                    <option value="Youth Leader">Youth Leader</option>
                    <option value="General Diaspora Member">General Diaspora Member</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Village / Sub-Clan</label>
                  <select
                    value={newContact.village}
                    onChange={(e) => setNewContact({ ...newContact, village: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-amber-300"
                  >
                    {INITIAL_UZAIRUE_VILLAGES.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-slate-300 font-bold mb-1">Diaspora Chapter</label>
                  <select
                    value={newContact.diasporaChapter}
                    onChange={(e) => setNewContact({ ...newContact, diasporaChapter: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-amber-300"
                  >
                    {INITIAL_DIASPORA_CHAPTERS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Email Address *</label>
                  <input
                    type="email"
                    required
                    placeholder="name@uzairue.org"
                    value={newContact.email}
                    onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Phone Number</label>
                  <input
                    type="text"
                    placeholder="+234 803 000 0000"
                    value={newContact.phone}
                    onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Organization / Association</label>
                <input
                  type="text"
                  placeholder="e.g. Uzairue Cultural Union UK"
                  value={newContact.organization}
                  onChange={(e) => setNewContact({ ...newContact, organization: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Notes / Committee Role</label>
                <textarea
                  rows={2}
                  placeholder="Optional details or responsibilities"
                  value={newContact.notes}
                  onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white"
                />
              </div>

              <div className="pt-3 border-t border-slate-800 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 bg-slate-800 text-slate-300 rounded-lg"
                >
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2 bg-amber-500 text-slate-950 font-bold rounded-lg shadow">
                  Save Contact
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsDirectory;
